import { App } from '@capacitor/app';
import api from './api';
import localDB from './localDB';
import networkService from './networkService';
import notificationService from './notificationService';
import { withOptionalRequestData } from '../utils/requestConfig';
import { decodeRequestData } from '../utils/requestData';

const SYNC_TIMEOUT_MS = 90000;
const MAX_RETRY_DELAY_MS = 60 * 60 * 1000;

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.syncInterval = null;
    this.syncListeners = [];
    this.initPromise = null;
    this.networkListener = null;
    this.appListener = null;
    this.authListener = null;
  }

  async init() {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.initialize();
    return this.initPromise;
  }

  async initialize() {
    await localDB.init();
    await notificationService.init();
    await notificationService.refreshTasks();

    this.networkListener = async (isOnline) => {
      if (isOnline && this.hasAuthToken()) {
        await this.startAutoSync();
        void this.syncAll({ force: true, source: 'network' });
      } else {
        await this.stopAutoSync();
        this.notifySyncListeners({ status: isOnline ? 'auth_required' : 'offline' });
      }
    };
    networkService.addListener(this.networkListener);

    try {
      this.appListener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive && this.hasAuthToken()) {
          void this.syncAll({ force: true, source: 'resume' });
        }
      });
    } catch (error) {
      console.warn('App resume sync unavailable:', error);
    }

    window.addEventListener('focus', this.handleFocus);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.authListener = (event) => {
      if (event.detail?.authenticated && this.hasAuthToken()) {
        void this.startAutoSync();
        void this.syncAll({ force: true, source: 'auth' });
      } else {
        void this.stopAutoSync();
        this.notifySyncListeners({ status: 'auth_required' });
      }
    };
    window.addEventListener('crm:auth-changed', this.authListener);

    if (this.hasAuthToken()) {
      await this.startAutoSync();
      if (await networkService.isConnected()) {
        void this.syncAll({ force: true, source: 'startup' });
      }
    }
    return true;
  }

  hasAuthToken() {
    return Boolean(localStorage.getItem('token'));
  }

  handleFocus = () => {
    if (this.hasAuthToken()) {
      void this.syncAll({ force: true, source: 'focus' });
    }
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && this.hasAuthToken()) {
      void this.syncAll({ force: true, source: 'visibility' });
    }
  };

  async startAutoSync() {
    if (this.syncInterval) return;
    const interval = await localDB.getSyncSetting('syncInterval', 30000);
    this.syncInterval = window.setInterval(() => {
      if (!this.isSyncing) void this.syncAll();
    }, Math.max(10000, Number(interval) || 30000));
  }

  async stopAutoSync() {
    if (!this.syncInterval) return;
    window.clearInterval(this.syncInterval);
    this.syncInterval = null;
  }

  normalizeQueueUrl(url) {
    if (typeof url !== 'string') return '/';
    let path = url.replace(/^https?:\/\/[^/]+/, '').trim() || '/';
    if (path.startsWith('/api/')) path = path.slice(4);
    if (path === '/api') path = '/';
    return path || '/';
  }

  async runAfterAction(action, serverTaskId) {
    const routes = {
      complete: { method: 'POST', url: `/tasks/${serverTaskId}/complete` },
      wait: { method: 'POST', url: `/tasks/${serverTaskId}/wait`, data: action.data || {} },
      history: { method: 'POST', url: `/tasks/${serverTaskId}/history`, data: action.data || {} },
    };
    const request = routes[action.type];
    if (!request) return null;
    const response = await api.request({
      ...request,
      headers: { 'X-Operation-Id': action.operation_id },
      timeout: 15000,
      _fromSyncQueue: true,
      _offlineHandled: true,
    });
    return response?.data;
  }

  async acknowledgeTaskOperation(item, responseData) {
    const path = this.normalizeQueueUrl(item.url);

    if (item.method === 'POST' && path === '/tasks') {
      const serverTask = responseData;
      if (!serverTask?.id) throw new Error('Сервер не вернул ID созданной задачи');
      await localDB.resolvePendingTask(item.entity_local_id, serverTask);

      let latestTask = serverTask;
      for (const action of (item.after_actions || [])) {
        const actionResult = await this.runAfterAction(action, serverTask.id);
        if (actionResult?.task) latestTask = actionResult.task;
      }
      await localDB.addTask(latestTask, false, true);
      return;
    }

    if (item.method === 'DELETE' && /^\/tasks\/\d+\/?$/.test(path)) {
      const taskId = path.match(/\/tasks\/(\d+)/)?.[1];
      if (taskId) await localDB.deleteTask(taskId).catch(() => {});
      return;
    }

    const task = responseData?.task || (
      responseData?.id && !responseData?.message ? responseData : null
    );
    if (task?.id) {
      await localDB.addTask(task, false, true);
      return;
    }

    const taskId = path.match(/\/tasks\/(\d+)/)?.[1];
    const itemData = decodeRequestData(item.data);
    if (taskId && itemData && item.method === 'PUT') {
      await localDB.updateTask(taskId, {
        ...itemData,
        local_updated: false,
        sync_status: 'synced',
        synced_at: new Date().toISOString(),
      }, false).catch(() => {});
    }
  }

  async syncQueue({ force = false } = {}) {
    const queue = await localDB.getSyncQueue();
    const now = Date.now();
    let authRequired = false;

    for (const item of queue) {
      const retryAt = item.next_retry_at ? new Date(item.next_retry_at).getTime() : 0;
      if (!force && retryAt > now) continue;

      await localDB.updateSyncQueueItem(item.id, {
        status: 'syncing',
        updated_at: new Date().toISOString(),
      });

      try {
        const requestConfig = withOptionalRequestData({
          method: (item.method || 'POST').toUpperCase(),
          url: this.normalizeQueueUrl(item.url),
          headers: {
            ...(item.headers || {}),
            'X-Operation-Id': item.operation_id,
          },
          timeout: 15000,
          _fromSyncQueue: true,
          _offlineHandled: true,
        }, decodeRequestData(item.data));
        const response = await api.request(requestConfig);

        if (item.entity_type === 'task') {
          await this.acknowledgeTaskOperation(item, response?.data);
        }
        await localDB.removeFromSyncQueue(item.id);
      } catch (error) {
        const retries = Number(item.retries || 0) + 1;
        const retryDelay = error.response?.status === 409
          ? 2000
          : Math.min(MAX_RETRY_DELAY_MS, 5000 * (2 ** Math.min(retries - 1, 10)));
        const status = error.response?.status === 401 ? 'auth_required' : 'retrying';

        await localDB.updateSyncQueueItem(item.id, {
          retries,
          status,
          error: error.response?.data?.error || error.message || 'Ошибка синхронизации',
          next_retry_at: new Date(Date.now() + retryDelay).toISOString(),
          updated_at: new Date().toISOString(),
        });

        if (error.response?.status === 401) {
          authRequired = true;
          break;
        }

        if (!error.response || error.response.status >= 500) {
          break;
        }
      }
    }

    return { authRequired };
  }

  async pullFromServer() {
    const [tasksResponse, clientsResponse, financesResponse] = await Promise.all([
      api.get('/tasks', { _offlineHandled: true }),
      api.get('/clients', { _offlineHandled: true }),
      api.get('/finances', { _offlineHandled: true }),
    ]);

    const tasks = Array.isArray(tasksResponse?.data) ? tasksResponse.data : [];
    for (const task of tasks) await localDB.addTask(task, false);

    const clients = Array.isArray(clientsResponse?.data) ? clientsResponse.data : [];
    for (const client of clients) await localDB.addClient(client, false);

    const finances = Array.isArray(financesResponse?.data) ? financesResponse.data : [];
    for (const finance of finances) await localDB.addFinance(finance, false);

    await notificationService.refreshTasks(await localDB.getTasks());
  }

  async syncAll(options = {}) {
    if (this.isSyncing) return;
    if (!this.hasAuthToken()) {
      await this.stopAutoSync();
      this.notifySyncListeners({ status: 'auth_required' });
      return;
    }
    if (!(await networkService.isConnected())) {
      this.notifySyncListeners({ status: 'offline' });
      return;
    }

    this.isSyncing = true;
    this.notifySyncListeners({ status: 'syncing', progress: 0 });
    let timeoutId;

    try {
      const timeout = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('Превышено время синхронизации')), SYNC_TIMEOUT_MS);
      });
      await Promise.race([
        (async () => {
          const queueResult = await this.syncQueue(options);
          if (queueResult.authRequired || !this.hasAuthToken()) {
            const authError = new Error('Требуется повторный вход в CRM');
            authError.code = 'AUTH_REQUIRED';
            throw authError;
          }
          await this.pullFromServer();
          await localDB.setSyncSetting('lastSync', new Date().toISOString());
        })(),
        timeout,
      ]);
      window.dispatchEvent(new CustomEvent('crm:sync-completed'));
      this.notifySyncListeners({ status: 'completed', progress: 100 });
    } catch (error) {
      console.error('Sync error:', error);
      this.notifySyncListeners({
        status: error.code === 'AUTH_REQUIRED' ? 'auth_required' : 'error',
        error: error.message,
      });
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
      this.isSyncing = false;
    }
  }

  addSyncListener(callback) {
    if (!this.syncListeners.includes(callback)) this.syncListeners.push(callback);
  }

  removeSyncListener(callback) {
    this.syncListeners = this.syncListeners.filter((listener) => listener !== callback);
  }

  notifySyncListeners(status) {
    this.syncListeners.forEach((listener) => {
      try {
        listener(status);
      } catch (error) {
        console.error('Sync listener error:', error);
      }
    });
  }

  async getSyncStatus() {
    return {
      isOnline: await networkService.isConnected(),
      isSyncing: this.isSyncing,
      pendingCount: await localDB.getQueueCount(),
      lastSync: await localDB.getSyncSetting('lastSync'),
    };
  }

  async clearQueue() {
    // Очередь является журналом несинхронизированных действий и не удаляется вручную.
    return false;
  }
}

const syncService = new SyncService();

export default syncService;
